import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Form,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "../../utills/api";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import logoLight from "../../assets/images/logo-light.png";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ChangePassword = () => {
  document.title="HMS | Change Password";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Please enter your old password"),
      newPassword: Yup.string()
        .required("Please enter a new password")
        .min(6, "Password must be at least 6 characters"),
      confirmPassword: Yup.string()
        .required("Please confirm your new password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),
    onSubmit: async (values, { setErrors, resetForm }) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("You are not logged in!");
          navigate("/login");
          return;
        }

        const res = await axios.put(
          "/auth/change-password"
,
          {
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success("Password updated successfully!");
        resetForm();
        setTimeout(() => navigate("/login"), 1500);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error("Something went wrong!");
        }
        setErrors({ oldPassword: "Invalid old password" });
      } finally {
        setLoading(false);
      }
    },
  });

  document.title = "Change Password | Yatranest";

  return (
    <ParticlesAuth>
      <div className="auth-page-content mt-lg-5">
        <ToastContainer />
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <Link to="/" className="d-inline-block auth-logo">
                  <img src={logoLight} alt="" height="40" />
                </Link>
                <p className="mt-3 fs-15 fw-medium">
                  Change your password securely
                </p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">Change Password</h5>
                    <p className="text-muted">Enter your old and new password</p>
                  </div>
                  <div className="p-2 mt-4">
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                    >
                      <div className="mb-3">
                        <Label htmlFor="oldPassword">Old Password</Label>
                        <Input
                          type="password"
                          name="oldPassword"
                          placeholder="Enter old password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.oldPassword}
                          invalid={
                            validation.touched.oldPassword &&
                            validation.errors.oldPassword
                              ? true
                              : false
                          }
                        />
                        {validation.touched.oldPassword &&
                        validation.errors.oldPassword ? (
                          <FormFeedback>
                            {validation.errors.oldPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                          type="password"
                          name="newPassword"
                          placeholder="Enter new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.newPassword}
                          invalid={
                            validation.touched.newPassword &&
                            validation.errors.newPassword
                              ? true
                              : false
                          }
                        />
                        {validation.touched.newPassword &&
                        validation.errors.newPassword ? (
                          <FormFeedback>
                            {validation.errors.newPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="confirmPassword">
                          Confirm Password
                        </Label>
                        <Input
                          type="password"
                          name="confirmPassword"
                          placeholder="Confirm new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword}
                          invalid={
                            validation.touched.confirmPassword &&
                            validation.errors.confirmPassword
                              ? true
                              : false
                          }
                        />
                        {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword ? (
                          <FormFeedback>
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-4">
                        <Button
                          color="secondary"
                          className="w-100"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Update Password"}
                        </Button>
                      </div>

                      <div className="mt-3 text-center">
                        <Link
                          to="/login"
                          className="fw-semibold text-primary text-decoration-underline"
                        >
                          Back to Login
                        </Link>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default ChangePassword;
